<template>
    <div class="pricing-cards" :class="{ 'pricing-cards--loading': isLoading }">
        <header class="pricing-cards__header">
            <p class="pricing-cards__eyebrow">{{ $t('pages.practice.billing.pricing_eyebrow') }}</p>
            <h2 class="pricing-cards__headline">{{ $t('pages.practice.billing.pricing_headline') }}</h2>
        </header>

        <div class="pricing-cards__grid">
            <article
                v-for="plan in plans"
                :key="plan.code"
                class="pricing-card"
                :class="{
                    'pricing-card--popular': isMostPopular(plan),
                    'pricing-card--current': isCurrentPlan(plan),
                }"
            >
                <span v-if="isMostPopular(plan)" class="pricing-card__badge">MOST POPULAR</span>
                <span v-if="isCurrentPlan(plan)" class="pricing-card__current">
                    Current Plan
                    <span v-if="isTrialActive">(TRIAL)</span>
                </span>

                <h3 class="pricing-card__name">{{ displayName(plan) }}</h3>

                <div class="pricing-card__price">
                    <span class="pricing-card__price-value">{{ formatPriceDisplay(plan) }}</span>
                    <span v-if="showPerMonth(plan)" class="pricing-card__price-period">/mo</span>
                </div>

                <p class="pricing-card__summary">{{ formatPlanLimitsSummary(plan) }}</p>

                <button
                    v-if="showButtons"
                    type="button"
                    class="pricing-card__cta"
                    :class="{ 'pricing-card__cta--primary': isMostPopular(plan) }"
                    :disabled="isCurrentPlan(plan) && !isCustomPlan(plan.code)"
                    @click="onSelect(plan)"
                >
                    {{ getButtonLabel(plan) }}
                </button>
            </article>
        </div>

        <p class="pricing-cards__footnote">{{ $t('pages.practice.billing.pricing_footnote') }}</p>
    </div>
</template>

<script>
    import PracticesAPI from 'api/PracticesAPI'
    import PracticePlanEnum from '@/enums/PracticePlanEnum'
    import {
        formatPlanLimitsSummary,
        formatPriceDisplay,
        getCtaLabel,
        getEffectivePlanCode,
        getFallbackPlans,
        getSalesMailto,
        hasActiveTrial,
        isCustomPlan,
        isMostPopular,
        isPracticeOnPaidPlan,
        mapLegacySubscriptionToPlanCode,
    } from 'services/billingPlanHelpers'

    export default {
        props: {
            showButtons: { type: Boolean, default: true },
            plansProp: { type: Array, default: null },
            useFallback: { type: Boolean, default: false },
        },

        data() {
            return {
                plans: [],
                currentPlanCode: null,
                isLoading: false,
            }
        },

        computed: {
            practice() {
                return this.$auth.getPractice?.() || {}
            },
            isTrialActive() {
                return hasActiveTrial(this.practice) || this.practice.subscription === PracticePlanEnum.TRIAL_PERIOD
            },
            isPaid() {
                return isPracticeOnPaidPlan(this.practice)
            },
        },

        methods: {
            formatPlanLimitsSummary,
            formatPriceDisplay,
            isMostPopular,
            isCustomPlan,

            displayName(plan) {
                if (plan.code === PracticePlanEnum.FREE) {
                    return 'Network'
                }
                if (plan.code === PracticePlanEnum.CUSTOM) {
                    return 'Enterprise'
                }
                return plan.name
            },

            showPerMonth(plan) {
                return plan.monthlyPrice != null && plan.monthlyPrice > 0 && !plan.hasTieredLocations
            },

            getButtonLabel(plan) {
                return getCtaLabel(plan, { isCurrent: this.isCurrentPlan(plan), isPaid: this.isPaid })
            },

            isCurrentPlan(plan) {
                return plan.code === this.currentPlanCode
            },

            onSelect(plan) {
                if (isCustomPlan(plan.code)) {
                    window.location.href = getSalesMailto()
                    return
                }
                this.$emit('select-plan', { plan })
                this.$emit('upgrade', { plan })
            },

            resolveCurrentPlanCode(apiCurrentPlan) {
                if (apiCurrentPlan?.planCode) {
                    return apiCurrentPlan.planCode
                }
                return (
                    getEffectivePlanCode(this.practice) ||
                    mapLegacySubscriptionToPlanCode(this.practice.subscription, this.practice.practiceType)
                )
            },

            loadPlans() {
                if (this.plansProp?.length) {
                    this.plans = this.plansProp
                    this.currentPlanCode = this.resolveCurrentPlanCode()
                    return Promise.resolve()
                }

                if (this.useFallback || !this.$auth.isLoggedIn?.()) {
                    this.plans = getFallbackPlans(this.practice.practiceType)
                    this.currentPlanCode = this.resolveCurrentPlanCode()
                    return Promise.resolve()
                }

                this.isLoading = true
                return PracticesAPI.getPlans()
                    .then((response) => {
                        this.plans = response.plans || []
                        this.currentPlanCode = this.resolveCurrentPlanCode(response.currentPlan)
                    })
                    .catch(() => {
                        this.plans = getFallbackPlans(this.practice.practiceType)
                        this.currentPlanCode = this.resolveCurrentPlanCode()
                    })
                    .finally(() => {
                        this.isLoading = false
                    })
            },
        },

        watch: {
            plansProp: {
                deep: true,
                handler(value) {
                    if (value?.length) {
                        this.plans = value
                    }
                },
            },
        },

        mounted() {
            this.loadPlans()
        },
    }
</script>

<style scoped lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_mixins.scss';

    .pricing-cards {
        width: 100%;
        padding: 12px 8px 8px;
        background: #f7f9fc;
        border-radius: 8px;

        &--loading {
            opacity: 0.6;
            pointer-events: none;
        }

        &__header {
            text-align: center;
            margin: 4px 0 28px;
        }

        &__eyebrow {
            margin: 0 0 10px;
            color: $color--accent-1;
            font-family: 'Poppins', sans-serif;
            font-size: 12px;
            font-weight: 700;
            letter-spacing: 0.14em;
            text-transform: uppercase;
        }

        &__headline {
            margin: 0 auto;
            max-width: 720px;
            color: $color--accent-3;
            font-family: 'Poppins', sans-serif;
            font-size: 32px;
            font-weight: 700;
            line-height: 1.2;
            letter-spacing: -0.02em;

            @include breakpoint(smartphone-large) {
                font-size: 24px;
            }
        }

        &__grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
            gap: 18px;
            align-items: stretch;
            width: 100%;

            @media (min-width: 1280px) {
                grid-template-columns: repeat(5, minmax(240px, 1fr));
            }
        }

        &__footnote {
            margin: 24px auto 4px;
            max-width: 720px;
            text-align: center;
            color: #8a96a8;
            font-family: 'Poppins', sans-serif;
            font-size: 12px;
            font-weight: 400;
            line-height: 1.5;
        }
    }

    .pricing-card {
        position: relative;
        display: flex;
        flex-direction: column;
        min-height: 280px;
        padding: 32px 26px 24px;
        background: #ffffff;
        border: 1px solid #e8eef5;
        border-radius: 16px;
        box-shadow: 0 8px 24px rgba(16, 40, 80, 0.06);
        text-align: left;

        &--popular {
            border: 2px solid $color--accent-1;
            box-shadow: 0 10px 28px rgba(19, 179, 227, 0.18);
        }

        &__badge {
            position: absolute;
            top: 0;
            left: 50%;
            transform: translate(-50%, -50%);
            padding: 5px 12px;
            border-radius: 999px;
            background: $color--accent-1;
            color: #fff;
            font-family: 'Poppins', sans-serif;
            font-size: 10px;
            font-weight: 700;
            letter-spacing: 0.06em;
            white-space: nowrap;
            line-height: 1.2;
        }

        &__current {
            position: absolute;
            top: 12px;
            right: 14px;
            padding: 3px 10px;
            border-radius: 999px;
            background: #eef6ff;
            color: $color--accent-3;
            font-size: 10px;
            font-weight: 600;
        }

        &__name {
            margin: 0 0 16px;
            color: $color--accent-3;
            font-family: 'Poppins', sans-serif;
            font-size: 16px;
            font-weight: 700;
            line-height: 1.2;
        }

        &__price {
            display: flex;
            align-items: baseline;
            gap: 4px;
            margin-bottom: 14px;
        }

        &__price-value {
            color: $color--accent-3;
            font-family: 'Poppins', sans-serif;
            font-size: 40px;
            font-weight: 700;
            line-height: 1;
            letter-spacing: -0.02em;
        }

        &__price-period {
            color: #6b7a90;
            font-family: 'Poppins', sans-serif;
            font-size: 15px;
            font-weight: 500;
        }

        &__summary {
            margin: 0 0 24px;
            color: #6b7a90;
            font-family: 'Poppins', sans-serif;
            font-size: 14px;
            font-weight: 400;
            line-height: 1.45;
            min-height: 42px;
            flex-grow: 1;
        }

        &__cta {
            width: 100%;
            margin-top: auto;
            padding: 13px 16px;
            border: none;
            border-radius: 10px;
            background: #e8f7fc;
            color: $color--accent-3;
            font-family: 'Poppins', sans-serif;
            font-size: 14px;
            font-weight: 600;
            line-height: 1.2;
            cursor: pointer;
            transition: background 0.15s ease, box-shadow 0.15s ease;

            &:hover:not(:disabled) {
                background: #d7f1fa;
            }

            &:disabled {
                opacity: 0.55;
                cursor: default;
            }

            &--primary {
                background: $color--accent-1;
                color: #ffffff;
                box-shadow: 0 8px 20px rgba(19, 179, 227, 0.35);

                &:hover:not(:disabled) {
                    background: darken($color--accent-1, 4%);
                    box-shadow: 0 10px 24px rgba(19, 179, 227, 0.42);
                }
            }
        }
    }
</style>
